import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomStateChip from "../../components/CustomStateChip";
import Logger from "../../../services/Logger";
import DatamuseService from "../../../services/DatamuseService";
import DictionaryApiService from "../../system/integrate/DictionaryApiService";
import AdminGridBoardService from "../board/AdminGridBoardService";
import { gridBoardDetailPath } from "../board/gridBoardDetailPath";
import AdminGridPackService from "./AdminGridPackService";

const PACK_RETURN_TO = "/admin/puzzle/grid_pack";
const DRAFT = "DRAFT";
const PUBLISHED = "PUBLISHED";

function boardFromCells(cells) {
    return (cells || []).map((row) => (row || []).map((letter) => ({
        letter: letter ? String(letter).toUpperCase() : "",
        filler: false,
    })));
}

function hasEmptyCell(board) {
    return board.length === 0 || board.some((row) => row.some((cell) => !cell.letter));
}

function isPublishableState(state) {
    const value = String(state || "").toUpperCase();
    return value === DRAFT || value === "READY_TO_REVIEW" || value === "REVIEWED";
}

function puzzleWords(row) {
    if (Array.isArray(row?.words) && row.words.length > 0) {
        return row.words.map((word) => String(word).trim()).filter(Boolean);
    }
    return [];
}

function MiniBoard({ board }) {
    if (!board || board.length === 0) {
        return <Typography variant="caption" color="text.secondary">No grid</Typography>;
    }
    return (
        <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 0.75, bgcolor: "#fafafa" }}>
            {board.map((row, rowIndex) => (
                <Box key={rowIndex} sx={{ display: "flex" }}>
                    {row.map((cell, colIndex) => (
                        <Box
                            key={colIndex}
                            sx={{
                                width: 22,
                                height: 22,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                border: "1px solid",
                                borderColor: cell.letter ? "#bdbdbd" : "#e0e0e0",
                                color: cell.filler ? "#757575" : "#212121",
                                bgcolor: cell.letter ? "#fff" : "#e0e0e0",
                            }}
                        >
                            {cell.letter}
                        </Box>
                    ))}
                </Box>
            ))}
        </Box>
    );
}

function shortDate(value) {
    if (!value) {
        return "";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${month}/${day}`;
}

const infoChipSx = {
    minHeight: 28,
    fontWeight: 700,
    "& .MuiChip-label": { px: 0.75 },
};

const SEARCH_OPTIONS_KEY = "admin.gridPack.search";

function readSearchOptions() {
    const defaults = {
        packCode: "season_2027",
        keyword: "",
        view: "list",
        pageSize: 15,
        excludePacked: false,
    };
    try {
        const raw = localStorage.getItem(SEARCH_OPTIONS_KEY);
        if (!raw) {
            return defaults;
        }
        const parsed = JSON.parse(raw);
        const pageSize = [15, 25, 50].includes(Number(parsed.pageSize)) ? Number(parsed.pageSize) : 15;
        return {
            packCode: typeof parsed.packCode === "string" && parsed.packCode ? parsed.packCode : defaults.packCode,
            keyword: typeof parsed.keyword === "string" ? parsed.keyword : "",
            view: parsed.view === "card" ? "card" : "list",
            pageSize,
            excludePacked: Boolean(parsed.excludePacked),
        };
    } catch (error) {
        Logger.error("Error reading grid pack search options:", error);
        return defaults;
    }
}

function writeSearchOptions(options) {
    try {
        localStorage.setItem(SEARCH_OPTIONS_KEY, JSON.stringify(options));
    } catch (error) {
        Logger.error("Error saving grid pack search options:", error);
    }
}

function sizeLabel(row) {
    const rows = Number(row?.gridRows);
    const cols = Number(row?.gridCols);
    if (!rows || !cols) {
        return "";
    }
    return `${rows}×${cols}`;
}

export default function AdminGridPackMain() {
    const navigate = useNavigate();
    const [stored] = useState(readSearchOptions);
    const [packs, setPacks] = useState([]);
    const [packCode, setPackCode] = useState(stored.packCode);
    const [members, setMembers] = useState([]);
    const [minis, setMinis] = useState([]);
    const [miniTotal, setMiniTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(stored.pageSize);
    const [keyword, setKeyword] = useState(stored.keyword);
    const [appliedKeyword, setAppliedKeyword] = useState(stored.keyword);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState("");
    const [snackOpen, setSnackOpen] = useState(false);
    const [snackMessage, setSnackMessage] = useState("");
    const [snackSeverity, setSnackSeverity] = useState("success");
    const [miniView, setMiniView] = useState(stored.view);
    const [excludePacked, setExcludePacked] = useState(stored.excludePacked);
    const [publishTarget, setPublishTarget] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [reorderOpen, setReorderOpen] = useState(false);
    const [reordering, setReordering] = useState(false);
    const [loadingPublish, setLoadingPublish] = useState(false);
    /** id -> { full, bannedOk, checking, bannedCount } */
    const [validityById, setValidityById] = useState({});

    const notify = (severity, message) => {
        setSnackSeverity(severity);
        setSnackMessage(message);
        setSnackOpen(true);
    };

    const evaluateValidity = useCallback(async (rows) => {
        const list = Array.isArray(rows) ? rows : [];
        if (list.length === 0) {
            return;
        }
        const pending = {};
        list.forEach((row) => {
            if (!row?.id) {
                return;
            }
            const full = !hasEmptyCell(boardFromCells(row.cells));
            pending[row.id] = {
                full,
                bannedOk: false,
                checking: full,
                bannedCount: 0,
            };
        });
        setValidityById((current) => ({ ...current, ...pending }));

        await Promise.all(list.map(async (row) => {
            if (!row?.id) {
                return;
            }
            const full = !hasEmptyCell(boardFromCells(row.cells));
            if (!full) {
                setValidityById((current) => ({
                    ...current,
                    [row.id]: { full: false, bannedOk: false, checking: false, bannedCount: 0 },
                }));
                return;
            }
            try {
                const words = puzzleWords(row);
                const result = await DictionaryApiService.checkBannedWords(
                    words,
                    row.language || "english",
                    Boolean(row.isKids),
                    row.difficultyLevel || null
                );
                const bannedCount = Array.isArray(result?.bannedWords) ? result.bannedWords.length : 0;
                setValidityById((current) => ({
                    ...current,
                    [row.id]: {
                        full: true,
                        bannedOk: bannedCount === 0,
                        checking: false,
                        bannedCount,
                    },
                }));
            } catch (error) {
                Logger.error("Banned-word check failed for pack puzzle:", error);
                setValidityById((current) => ({
                    ...current,
                    [row.id]: { full: true, bannedOk: false, checking: false, bannedCount: -1 },
                }));
            }
        }));
    }, []);

    const loadPacks = useCallback(async () => {
        const items = await AdminGridPackService.list();
        setPacks(items);
        setPackCode((current) => {
            if (items.some((pack) => pack.code === current)) {
                return current;
            }
            return items[0]?.code || "season_2027";
        });
        return items;
    }, []);

    const loadBody = useCallback(async (code, nextPage, nextSize, nextKeyword, nextExcludePacked) => {
        if (!code) {
            return;
        }
        setLoading(true);
        try {
            const [memberRows, miniPage] = await Promise.all([
                AdminGridPackService.members(code),
                AdminGridPackService.mini(code, nextPage, nextSize, nextKeyword, nextExcludePacked),
            ]);
            setMembers(memberRows);
            setMinis(miniPage?.content || []);
            setMiniTotal(Number(miniPage?.total) || 0);
            await evaluateValidity([...(memberRows || []), ...(miniPage?.content || [])]);
        } finally {
            setLoading(false);
        }
    }, [evaluateValidity]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const items = await loadPacks();
                if (cancelled) {
                    return;
                }
                const code = items.some((pack) => pack.code === stored.packCode)
                    ? stored.packCode
                    : (items[0]?.code || "");
                if (code) {
                    setPackCode(code);
                    await loadBody(code, 0, stored.pageSize, stored.keyword, stored.excludePacked);
                }
            } catch (error) {
                Logger.error("Error loading grid packs:", error);
                if (!cancelled) {
                    notify("error", error.message || "Could not load packs. Run create_grid_pack.sql first.");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadPacks, loadBody, stored]);

    useEffect(() => {
        writeSearchOptions({
            packCode,
            keyword: appliedKeyword,
            view: miniView,
            pageSize,
            excludePacked,
        });
    }, [packCode, appliedKeyword, miniView, pageSize, excludePacked]);

    const selectPack = async (code) => {
        if (!code || code === packCode) {
            return;
        }
        setPackCode(code);
        setPage(0);
        try {
            await loadBody(code, 0, pageSize, appliedKeyword, excludePacked);
        } catch (error) {
            notify("error", error.message || "Could not load pack");
        }
    };

    const search = async () => {
        const next = keyword.trim();
        setAppliedKeyword(next);
        setPage(0);
        try {
            await loadBody(packCode, 0, pageSize, next, excludePacked);
        } catch (error) {
            notify("error", error.message || "Search failed");
        }
    };

    const toggleExcludePacked = async (checked) => {
        setExcludePacked(checked);
        setPage(0);
        try {
            await loadBody(packCode, 0, pageSize, appliedKeyword, checked);
        } catch (error) {
            notify("error", error.message || "Could not load puzzles");
        }
    };

    const changePage = async (nextPage, nextSize) => {
        setPage(nextPage);
        setPageSize(nextSize);
        try {
            await loadBody(packCode, nextPage, nextSize, appliedKeyword, excludePacked);
        } catch (error) {
            notify("error", error.message || "Could not load puzzles");
        }
    };

    const addPuzzle = async (tplId) => {
        setBusyId(tplId);
        try {
            const result = await AdminGridPackService.add(packCode, tplId);
            notify("success", result.message || "Added to pack");
            await loadPacks();
            await loadBody(packCode, page, pageSize, appliedKeyword, excludePacked);
        } catch (error) {
            notify("error", error.message || "Could not add puzzle");
        } finally {
            setBusyId("");
        }
    };

    const removePuzzle = async (tplId) => {
        setBusyId(tplId);
        try {
            const result = await AdminGridPackService.remove(packCode, tplId);
            notify("success", result.message || "Removed from pack");
            await loadPacks();
            await loadBody(packCode, page, pageSize, appliedKeyword, excludePacked);
        } catch (error) {
            notify("error", error.message || "Could not remove puzzle");
        } finally {
            setBusyId("");
        }
    };

    const fillPuzzle = async (row) => {
        if (!hasEmptyCell(boardFromCells(row.cells))) {
            notify("info", "This grid has no empty cells");
            return;
        }
        setBusyId(row.id);
        try {
            const result = await AdminGridPackService.fill(row.id);
            const updated = { ...row, cells: result.cells || row.cells };
            setMinis((current) => current.map((item) => (
                item.id === row.id ? { ...item, cells: result.cells || item.cells } : item
            )));
            setMembers((current) => current.map((item) => (
                item.id === row.id ? { ...item, cells: result.cells || item.cells } : item
            )));
            notify("success", result.message || "Empty cells filled");
            await evaluateValidity([updated]);
        } catch (error) {
            notify("error", error.message || "Could not fill empty cells");
        } finally {
            setBusyId("");
        }
    };

    const rowValidity = (row) => {
        const cached = validityById[row?.id];
        if (cached) {
            return cached;
        }
        const full = !hasEmptyCell(boardFromCells(row?.cells));
        return { full, bannedOk: false, checking: false, bannedCount: 0 };
    };

    const canPublishRow = (row) => {
        const validity = rowValidity(row);
        return isPublishableState(row?.state)
            && validity.full
            && validity.bannedOk
            && !validity.checking;
    };

    const reorderLevels = async () => {
        if (!packCode) {
            return;
        }
        setReordering(true);
        try {
            const result = await AdminGridPackService.reorder(packCode);
            notify("success", result.message || "Levels re-ordered");
            await loadPacks();
            await loadBody(packCode, page, pageSize, appliedKeyword, excludePacked);
        } catch (error) {
            notify("error", error.message || "Could not re-order levels");
        } finally {
            setReordering(false);
        }
    };

    const selected = packs.find((pack) => pack.code === packCode);

    const openDetail = (row) => {
        if (!row?.id) {
            return;
        }
        navigate(gridBoardDetailPath(row, { returnTo: PACK_RETURN_TO }));
    };

    const publishPuzzle = async () => {
        const row = publishTarget;
        if (!row?.id) {
            return;
        }
        setLoadingPublish(true);
        try {
            if (hasEmptyCell(boardFromCells(row.cells))) {
                notify("error", "Fill all empty cells before publishing");
                return;
            }
            const words = puzzleWords(row);
            const banned = await DictionaryApiService.checkBannedWords(
                words,
                row.language || "english",
                Boolean(row.isKids),
                row.difficultyLevel || null
            );
            if (Array.isArray(banned?.bannedWords) && banned.bannedWords.length > 0) {
                const sample = banned.bannedWords
                    .slice(0, 5)
                    .map((item) => (typeof item === "string" ? item : item?.word))
                    .filter(Boolean)
                    .join(", ");
                notify("error", sample
                    ? `Cannot publish: banned words found (${sample})`
                    : "Cannot publish: banned words found");
                await evaluateValidity([{ ...row }]);
                return;
            }
            const details = await DatamuseService.requestWordScores(words);
            const result = await AdminGridBoardService.publishHwTpl(
                row.id,
                "",
                details,
                false
            );
            if (result?.code === "FAILURE") {
                notify("error", result.message || "Publish failed");
            } else {
                notify("success", "Puzzle published");
                setMinis((current) => current.map((item) => (
                    item.id === row.id ? { ...item, state: PUBLISHED } : item
                )));
                setMembers((current) => current.map((item) => (
                    item.id === row.id ? { ...item, state: PUBLISHED } : item
                )));
            }
        } catch (error) {
            Logger.error("Error publishing pack puzzle:", error);
            notify("error", error.message || "Could not publish puzzle");
        } finally {
            setLoadingPublish(false);
            setPublishTarget(null);
        }
    };

    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5">Grid packs (mini)</Typography>
            <Typography variant="body2" color="text.secondary">
                Mini word search only (5×5 through 9×9). Season 2027 and Classic are the first packs.
            </Typography>
            <ToggleButtonGroup
                exclusive
                value={packCode}
                onChange={(_event, value) => {
                    if (value) {
                        selectPack(value);
                    }
                }}
            >
                {packs.map((pack) => (
                    <ToggleButton key={pack.code} value={pack.code}>
                        {pack.name} ({pack.puzzleCount}/{pack.maxLevel})
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {selected ? selected.name : "Pack"}
                    </Typography>
                    {packCode === "season_2027" ? (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={reordering || members.length === 0}
                            onClick={() => setReorderOpen(true)}
                        >
                            Re-order levels
                        </Button>
                    ) : null}
                </Box>
                {packs.length === 0 && !loading && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        No packs yet. Run docs/sql-v2/create_grid_pack.sql, then restart the backend.
                    </Typography>
                )}
                {loading && members.length === 0 ? (
                    <CircularProgress size={24} />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Level</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Size</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {members.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5}>No puzzles in this pack yet.</TableCell>
                                </TableRow>
                            ) : members.map((row) => {
                                const validity = rowValidity(row);
                                const memberCanPublish = canPublishRow(row);
                                const publishHint = memberCanPublish
                                    ? "Publish"
                                    : validity.checking
                                        ? "Checking banned words…"
                                        : !validity.full
                                            ? "Fill empty cells first"
                                            : !validity.bannedOk
                                                ? "Banned words found — not valid"
                                                : "Publish when DRAFT/READY_TO_REVIEW/REVIEWED and Valid";
                                return (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{ cursor: "pointer" }}
                                    onClick={() => openDetail(row)}
                                >
                                    <TableCell>{row.puzzleLevel}</TableCell>
                                    <TableCell>{row.title}</TableCell>
                                    <TableCell>{sizeLabel(row)}</TableCell>
                                    <TableCell>{row.state}</TableCell>
                                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                                        <Box sx={{ display: "inline-flex", gap: 0.5, alignItems: "center" }}>
                                            <Tooltip title={publishHint}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        aria-label="Publish"
                                                        disabled={!memberCanPublish || busyId === row.id || loadingPublish}
                                                        onClick={() => setPublishTarget(row)}
                                                    >
                                                        {loadingPublish && publishTarget?.id === row.id
                                                            ? <CircularProgress size={18} color="inherit" />
                                                            : <PublishIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Remove">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        color="warning"
                                                        aria-label="Remove"
                                                        disabled={busyId === row.id}
                                                        onClick={() => setRemoveTarget(row)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>Mini word search</Typography>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={miniView}
                        onChange={(_event, value) => {
                            if (value) {
                                setMiniView(value);
                            }
                        }}
                    >
                        <ToggleButton value="list" aria-label="List view">
                            <ViewListIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="card" aria-label="Card view">
                            <ViewModuleIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                size="small"
                                checked={excludePacked}
                                onChange={(event) => toggleExcludePacked(event.target.checked)}
                            />
                        )}
                        label="Exclude packed"
                    />
                    <TextField
                        size="small"
                        label="Title"
                        value={keyword}
                        onChange={(event) => setKeyword(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                search();
                            }
                        }}
                    />
                    <Button variant="outlined" onClick={search}>Search</Button>
                </Box>
                {miniView === "card" ? (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))",
                            gap: 2,
                        }}
                    >
                        {minis.length === 0 ? (
                            <Typography color="text.secondary">No mini word search puzzles.</Typography>
                        ) : minis.map((row) => {
                            const board = boardFromCells(row.cells);
                            const empty = hasEmptyCell(board);
                            const validity = rowValidity(row);
                            const isValid = validity.full && validity.bannedOk && !validity.checking;
                            const addTitle = row.inPack ? "In pack" : (empty ? "Fill all cells before adding" : "Add");
                            const dateLabel = shortDate(row.publishedAt || row.createdAt);
                            const wordCount = Number(row.wordCount);
                            const canPublish = canPublishRow(row);
                            const publishHint = canPublish
                                ? "Publish"
                                : validity.checking
                                    ? "Checking banned words…"
                                    : !validity.full
                                        ? "Fill empty cells first"
                                        : !validity.bannedOk
                                            ? "Banned words found — not valid"
                                            : "Publish when DRAFT/READY_TO_REVIEW/REVIEWED and Valid";
                            return (
                                <Paper
                                    key={row.id}
                                    elevation={2}
                                    onClick={() => openDetail(row)}
                                    sx={{
                                        p: 1.5,
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        height: "100%",
                                        cursor: "pointer",
                                        transition: "box-shadow 0.2s ease, transform 0.2s ease",
                                        "&:hover": {
                                            boxShadow: 6,
                                            transform: "translateY(-2px)",
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minHeight: 40 }}>
                                        <Typography
                                            variant="subtitle2"
                                            title={row.title || "Untitled"}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontWeight: 600,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {row.title || "Untitled"}
                                        </Typography>
                                        {row.state ? <CustomStateChip label={row.state} size="small" /> : null}
                                    </Box>
                                    <MiniBoard board={board} />
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden", flexWrap: "wrap" }}>
                                        {validity.checking ? (
                                            <Chip
                                                size="small"
                                                label="Checking…"
                                                variant="outlined"
                                                sx={infoChipSx}
                                            />
                                        ) : isValid ? (
                                            <Chip
                                                size="small"
                                                icon={<CheckCircleIcon />}
                                                label="Valid"
                                                color="success"
                                                sx={infoChipSx}
                                            />
                                        ) : !validity.full ? (
                                            <Chip
                                                size="small"
                                                label="Incomplete"
                                                color="warning"
                                                variant="outlined"
                                                sx={infoChipSx}
                                            />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label={validity.bannedCount > 0 ? `Banned (${validity.bannedCount})` : "Invalid"}
                                                color="error"
                                                variant="outlined"
                                                sx={infoChipSx}
                                            />
                                        )}
                                        <Chip size="small" label={sizeLabel(row) || "—"} variant="outlined" sx={infoChipSx} />
                                        {Number.isFinite(wordCount) ? (
                                            <Chip size="small" label={`${wordCount} wds`} variant="outlined" sx={infoChipSx} />
                                        ) : null}
                                        {dateLabel ? (
                                            <Chip size="small" label={dateLabel} variant="outlined" sx={infoChipSx} />
                                        ) : null}
                                    </Box>
                                    <Box
                                        sx={{ display: "flex", justifyContent: "center", gap: 0.25 }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        <Tooltip title="Fill random letters">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={!empty || busyId === row.id}
                                                    onClick={() => fillPuzzle(row)}
                                                >
                                                    <ShuffleIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={addTitle}>
                                            <span>
                                                <IconButton
                                                    color="primary"
                                                    size="small"
                                                    disabled={row.inPack || empty || busyId === row.id || !packCode}
                                                    onClick={() => addPuzzle(row.id)}
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title={publishHint}>
                                            <span>
                                                <IconButton
                                                    color="secondary"
                                                    size="small"
                                                    disabled={!canPublish || busyId === row.id || loadingPublish}
                                                    onClick={() => setPublishTarget(row)}
                                                >
                                                    {loadingPublish && publishTarget?.id === row.id
                                                        ? <CircularProgress size={18} color="inherit" />
                                                        : <PublishIcon fontSize="small" />}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Size</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {minis.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>No mini word search puzzles.</TableCell>
                            </TableRow>
                        ) : minis.map((row) => (
                            <TableRow
                                key={row.id}
                                hover
                                sx={{ cursor: "pointer" }}
                                onClick={() => openDetail(row)}
                            >
                                <TableCell>{row.title}</TableCell>
                                <TableCell>{sizeLabel(row)}</TableCell>
                                <TableCell>{row.state}</TableCell>
                                <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        title={hasEmptyCell(boardFromCells(row.cells)) ? "Fill all cells before adding" : "Add"}
                                        disabled={row.inPack || hasEmptyCell(boardFromCells(row.cells)) || busyId === row.id || !packCode}
                                        onClick={() => addPuzzle(row.id)}
                                    >
                                        {row.inPack ? "In pack" : "Add"}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                )}
                <TablePagination
                    component="div"
                    count={miniTotal}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[15, 25, 50]}
                    onPageChange={(_event, next) => changePage(next, pageSize)}
                    onRowsPerPageChange={(event) => changePage(0, Number(event.target.value))}
                />
            </Paper>
            <CustomSnackbar open={snackOpen} setOpen={setSnackOpen} message={snackMessage} severity={snackSeverity} />
            <ConfirmDialog
                open={Boolean(publishTarget)}
                setOpen={(open) => {
                    if (!open) {
                        setPublishTarget(null);
                    }
                }}
                title="Publish Puzzle"
                message="Publish this puzzle now?"
                targetName={publishTarget?.title || ""}
                targetId={publishTarget?.id || ""}
                callback={publishPuzzle}
            />
            <ConfirmDialog
                open={Boolean(removeTarget)}
                setOpen={(open) => {
                    if (!open) {
                        setRemoveTarget(null);
                    }
                }}
                title="Remove from pack"
                message="Remove this puzzle from the pack? Later levels will be renumbered."
                targetName={removeTarget?.title || ""}
                targetId={removeTarget?.id || ""}
                callback={(id) => {
                    setRemoveTarget(null);
                    if (id) {
                        removePuzzle(id);
                    }
                }}
            />
            <ConfirmDialog
                open={reorderOpen}
                setOpen={setReorderOpen}
                title="Re-order Season 2027"
                message="Smaller boards become the lower levels. When two puzzles have the same number of cells, the one with the higher total word score comes first."
                callback={reorderLevels}
            />
        </Box>
    );
}
